import { useNavigate } from "react-router-dom";
import { ConfidenceBar, ListingLinkButton, ScoreBadge } from "@/components/common/Primitives";
import { BpmCell, Cell, ValueCell } from "@/components/opportunities/tableCells";
import {
  countryName,
  dateOnly,
  eur,
  eurSigned,
  km,
  pct,
  vehicleSpec,
  vehicleTitle,
} from "@/lib/format";

export const OpportunityTableRow = ({ row, compact, onDialog, onOverride, onValue }) => {
  const navigate = useNavigate();
  const v = row.vehicle_snapshot || {};
  const h = row.headline || {};
  const costs = row.costs || {};
  const importCosts =
    (costs.other_costs_excl_purchase ?? 0) -
    (costs.lines?.find((l) => l.key === "transport")?.amount ?? 0) -
    (h.bpm ?? 0);

  return (
    <tr
      className="cursor-pointer"
      data-testid={`row-${row.listing_id}`}
      onClick={() => navigate(`/auto/${row.listing_id}`)}
    >
      <td className="max-w-[260px]">
        <div className="flex items-center gap-2">
          {v.image_urls?.[0] ? (
            <img
              src={v.image_urls[0]}
              alt=""
              className="h-9 w-14 shrink-0 rounded-sm border border-slate-200 object-cover"
            />
          ) : (
            <div className="flex h-9 w-14 shrink-0 items-center justify-center rounded-sm border border-dashed border-slate-300 text-[8px] text-slate-400">
              geen foto
            </div>
          )}
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-slate-900">
              {vehicleTitle(v)}
            </div>
            <div className="truncate text-[10.5px] text-slate-500">{vehicleSpec(v)}</div>
          </div>
        </div>
      </td>
      {!compact && <td className="num">{dateOnly(v.first_registration)}</td>}
      {!compact && <td className="num">{km(v.mileage_km)}</td>}
      <td className="text-[12px]">{countryName(v.country)}</td>
      {!compact && <td className="text-[12px]">{v.seller_type || "—"}</td>}
      {!compact && <td className="text-[11px] text-slate-500">{v.source}</td>}
      <td className="num font-semibold">
        {eur(v.price)}
        {v.price_change < 0 && (
          <div className="text-[10px] font-semibold text-emerald-700">
            Prijs verlaagd {eurSigned(v.price_change)}
          </div>
        )}
      </td>
      {!compact && (
        <td className="text-[11px]">
          {v.vat_status === "Onbekend" ? (
            <span className="text-amber-700">Controle vereist</span>
          ) : (
            v.vat_status
          )}
        </td>
      )}
      <BpmCell
        row={row}
        onExplain={() => onDialog({ type: "bpm", row })}
        onEdit={() => onOverride(row)}
      />
      {!compact && (
        <td className="num">{eur(costs.lines?.find((l) => l.key === "transport")?.amount)}</td>
      )}
      {!compact && <td className="num">{eur(importCosts)}</td>}
      <Cell
        className="num font-semibold"
        onClick={() => onDialog({ type: "cost", row })}
        testid={`landed-cell-${row.listing_id}`}
      >
        {eur(h.landed_cost)}
      </Cell>
      <ValueCell
        row={row}
        onExplain={() => onDialog({ type: "valuation", row })}
        onEdit={() => onValue(row)}
      />
      <Cell
        className={`num font-bold ${(h.contribution ?? 0) > 0 ? "text-emerald-700" : "text-red-700"}`}
        onClick={() => onDialog({ type: "cost", row })}
        testid={`contribution-cell-${row.listing_id}`}
      >
        {eurSigned(h.contribution, { fallback: "—" })}
      </Cell>
      <td className="num font-semibold">{pct(h.roi_pct, { fallback: "—" })}</td>
      {compact && (
        <td>
          <ConfidenceBar value={h.confidence_pct} label={null} />
        </td>
      )}
      <Cell onClick={() => onDialog({ type: "score", row })} testid={`score-cell-${row.listing_id}`}>
        <ScoreBadge score={h.deal_score} advice={h.advice} />
        {h.score_status === "VOORLOPIG" && (
          <span
            className="chip mt-0.5 border-amber-300 bg-amber-50 text-[9px] text-amber-800"
            data-testid={`preliminary-${row.listing_id}`}
          >
            Voorlopige kans
          </span>
        )}
        {h.score_status === "ONVOLDOENDE_DATA" && (
          <span
            className="chip mt-0.5 border-slate-300 bg-slate-50 text-[9px] text-slate-600"
            data-testid={`insufficient-${row.listing_id}`}
          >
            Onvoldoende marktdata
          </span>
        )}
      </Cell>
      <td>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid={`details-${row.listing_id}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/auto/${row.listing_id}`);
            }}
            className="rounded-sm border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Details
          </button>
          <ListingLinkButton url={v.url} testid={`listing-url-${row.listing_id}`} />
        </div>
      </td>
    </tr>
  );
};
